import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, OnInit, Output, signal, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { Station } from '@app/services/station.interface';
import { WEEKDAYS } from '@app/shared/utils/consts';
import { formatTime } from '@app/shared/utils';
import { LeafletModule } from '@bluehalo/ngx-leaflet';
import { ConnectorListComponent } from '../connector-list/connector-list.component';
import { AmenitiesListComponent } from '../amenities-list/amenities-list.component';
import dayjs from 'dayjs';
import { StationDetailsService } from '@app/pages/station-details/station-details.service';
import { EditChargerDialogComponent } from '@app/components/edit-charger-dialog/edit-charger-dialog.component';
import { MiniProfileComponent, MiniProfile } from '@app/components/mini-profile/mini-profile.component';
import { DevcycleService, FEATURE_FLAGS } from '@app/services/devcycle.service';

@Component({
  selector: 'app-station-info',
  standalone: true,
  imports: [
    LeafletModule,
    MatButtonModule,
    MatDividerModule,
    MatIconModule,
    CommonModule,
    ConnectorListComponent,
    AmenitiesListComponent,
    MiniProfileComponent,
  ],
  templateUrl: './station-info.component.html',
  styleUrl: './station-info.component.scss',
})
export class StationInfoComponent implements OnInit {
  @Input() station: Station = null;
  @Output() stationUpdated = new EventEmitter<void>();
  @ViewChild('profilesGrid') profilesGrid: ElementRef;

  readonly hoursOpenState = signal(false);
  readonly costsOpenState = signal(false);
  openHoursText = signal('');
  costsText = signal('');

  formatTime = formatTime;
  weekdays = WEEKDAYS;
  showAllAmenities = false;
  lastModifiedDate = '';
  connectorStatus = '';
  stationHours = this.station?.hours?.dayHours;

  showEditCharger = signal(false);
  showStationProfiles = signal(false);

  // Mock profile data for demonstration
  stationProfiles: MiniProfile[] = [
    {
      name: 'John Smith',
      companyName: 'EV Properties LLC',
      role: 'owner',
      avatarUrl: 'little_1.png',
      description: 'Property owner since 2020',
      phone: '+1 555-0123',
      email: 'contact@evproperties.com',
    },
    {
      name: 'ChargePoint ChargePoint',
      companyName: 'ChargePoint ChargePoint',
      role: 'installer',
      avatarUrl: 'little_2.png',
      description: 'Leading EV charging network',
      website: 'https://chargepoint.com',
      email: 'support@chargepoint.com',
    },
    {
      name: 'Tesla',
      companyName: 'Tesla',
      role: 'manufacturer',
      avatarUrl: 'little_3.png',
      description: 'Electric vehicles & energy',
      website: 'https://tesla.com',
    },
    {
      name: 'Electrify America',
      companyName: 'Electrify America',
      role: 'installer',
      avatarUrl: 'little_2.png',
      description: 'Fast charging network',
      website: 'https://electrifyamerica.com',
    },
  ];

  constructor(
    private stationDetailsService: StationDetailsService,
    private dialog: MatDialog,
    private devcycleService: DevcycleService,
  ) {}

  ngOnInit(): void {
    this.showEditCharger.set(this.devcycleService.getFeatureFlag(FEATURE_FLAGS.EDIT_CHARGER_ENABLE, false));
    this.devcycleService.onVariableUpdated(FEATURE_FLAGS.EDIT_CHARGER_ENABLE, (key, value) => {
      this.showEditCharger.set(value);
    });
    this.showStationProfiles.set(this.devcycleService.getFeatureFlag(FEATURE_FLAGS.SHOW_STATION_PROFILES, false));
    this.devcycleService.onVariableUpdated(FEATURE_FLAGS.SHOW_STATION_PROFILES, (key, value) => {
      this.showStationProfiles.set(value);
    });

    this.lastModifiedDate = dayjs(this.station?.updatedTs).format('MMMM D, YYYY');
    this.getOpenHoursText();
    this.getCostsText();

    const isAvailable = this.station?.chargers?.some(charger =>
      charger.connectors?.some(connector => connector.status === "AVAILABLE")
    );
    this.connectorStatus = isAvailable ? "Available" : "Unknown";

    if (this.station?.hours?.dayHours) {
      const filteredHours = {};
      Object.keys(this.station?.hours?.dayHours).forEach(day => {
        const filtered = this.station?.hours?.dayHours[day]?.filter(period => period?.open && period?.close);
        if (filtered?.length > 0) {
          filteredHours[day] = filtered;
        }
      });
      this.stationHours = filteredHours;
    }
  }

  get displayedAmenities() {
    return this.showAllAmenities
      ? this?.station?.amenities
      : this?.station?.amenities?.slice(0, 6);
  }

  toggleAmenities() {
    this.showAllAmenities = !this.showAllAmenities;
  }

  convertLowerCase(string: string) {
    return String(string).toLowerCase();
  }

  getOpenHoursText() {
    this.openHoursText.set(this.stationDetailsService.getOpenHoursText(this.station));
  }

  getCostsText() {
    this.costsText.set(this.stationDetailsService.getCostsText(this.station));
  }

  hasDayHoursEntries() {
    return !!this.stationHours && Object.values(this.stationHours).some(day => day && day.length > 0);
  }

  hasCostsEntries() {
    const pricingHours = this.station?.stationPricingHours?.pricingHours;
    return !!pricingHours && Object.values(pricingHours).some(day => day && day.length > 0);
  }

  openEditDialog() {
    const dialogRef = this.dialog.open(EditChargerDialogComponent, {
      maxWidth: '560px',
      width: '560px',
      maxHeight: '90vh',
      data: { station: this.station },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.updated) {
        this.stationUpdated.emit();
      }
    });
  }

  scrollProfiles(direction: 'left' | 'right') {
    const grid = this.profilesGrid.nativeElement;
    const scrollAmount = 160;
    if (direction === 'left') {
      grid.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
    } else {
      grid.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  }
}
